// Cloudflare Turnstile: the bot check in front of Supabase Auth (2026-10-21).
//
// Bots were calling Supabase's /signup endpoint directly from Tor exits with
// strangers' email addresses, so each one made us email a confirmation to
// someone who never asked (30 accounts, 16 to 20 October). Rate limits cannot
// stop one request an hour from a fresh IP; a CAPTCHA can. Supabase checks the
// token itself once "Enable CAPTCHA protection" is on in its Auth settings, so
// the secret lives there, never here.
//
// ⛔ ROLLOUT ORDER: deploy the forms with the site key set FIRST, then switch
// CAPTCHA on in Supabase. Switched on before the deploy, Supabase refuses every
// password sign-in, signup, reset and resend that carries no token.
//
// With no site key every helper here is inert and the forms behave exactly as
// before, which is what makes that order safe.
//
// Gated by Supabase (auth server `verifyCaptcha`): /signup, /recover, /resend,
// /otp, /magiclink and /token with grant_type=password. NOT gated: refresh,
// PKCE and id_token grants (Google sign-in), /verify (redeeming a link), and
// any request carrying a service-role credential, which is how the
// change-password re-auth check gets through without a second challenge.

import type { Locale } from "@/lib/locale";

/** Public by design: it is printed into every page that renders a widget. */
export const TURNSTILE_SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY?.trim() ?? "";

export const TURNSTILE_SCRIPT_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

export function turnstileEnabled(siteKey: string = TURNSTILE_SITE_KEY): boolean {
  return siteKey.length > 0;
}

/** Turnstile's own language codes for our locales. */
const LANGUAGE: Record<Locale, string> = {
  en: "en",
  vi: "vi",
  fr: "fr",
  "zh-Hans": "zh-cn",
  "zh-Hant": "zh-tw",
};

export function turnstileLanguage(locale: string): string {
  return LANGUAGE[locale as Locale] ?? "auto";
}

/**
 * The `options` fragment for a Supabase auth call. Empty without a token, so
 * an unconfigured site sends exactly the request it sent before.
 */
export function captchaOptions(token: string | null | undefined): { captchaToken?: string } {
  const t = typeof token === "string" ? token.trim() : "";
  return t ? { captchaToken: t } : {};
}

/** Cloudflare's cap on a token. Anything longer is not one. */
const MAX_TOKEN_LENGTH = 2048;

/** Reads the hidden form field a server action receives. */
export function captchaTokenFromForm(formData: FormData): string | null {
  const raw = formData.get(CAPTCHA_FIELD);
  if (typeof raw !== "string") return null;
  const t = raw.trim();
  return t && t.length <= MAX_TOKEN_LENGTH ? t : null;
}

export const CAPTCHA_FIELD = "captchaToken";

/** Supabase's error code for a missing, expired, reused or failed token. */
export function isCaptchaError(error: unknown): boolean {
  return (error as { code?: unknown } | null)?.code === "captcha_failed";
}

// ── Our own forms (/contact, guest /request) ────────────────────────────────
//
// Supabase checks tokens for the auth endpoints. Our own server actions have
// no Supabase in front of them, so they verify against Cloudflare themselves
// (`verifyTurnstileToken`, lib/turnstile-verify.ts) with `TURNSTILE_SECRET_KEY`,
// the same widget's secret. Without the secret the check is skipped, exactly
// like the site key on the client.

/** Cloudflare's siteverify answer, as far as we read it. */
export type SiteverifyResult = {
  success?: unknown;
  action?: unknown;
  "error-codes"?: unknown;
  metadata?: { result_with_testing_key?: unknown };
};

export type TurnstileVerdict = { ok: true } | { ok: false; reason: string };

/**
 * The decision on a siteverify answer. Pure, so it is tested without a network.
 * The ACTION must match the form's own: a token minted for the sign-in widget
 * is not one for /contact.
 */
export function turnstileVerdict(result: SiteverifyResult | null, expectedAction: string): TurnstileVerdict {
  if (!result || result.success !== true) {
    const codes = Array.isArray(result?.["error-codes"]) ? result["error-codes"].join(",") : "";
    return { ok: false, reason: codes || "rejected" };
  }
  // Cloudflare's public TEST secret answers with no action at all and flags the
  // answer itself. Only that secret produces the flag, and it is never the one
  // configured in production, so this exempts local testing and nothing else.
  const testing = result.metadata?.result_with_testing_key === true;
  if (!testing && result.action !== expectedAction) return { ok: false, reason: "action-mismatch" };
  return { ok: true };
}
